<script>

import updateImage from "@/mixins/updateImage";
import ImageUpload from "@/components/ImageUpload";
import ButtonComponent from "@/components/Button/ButtonComponent";
import Spinner from "@/components/parts/Spinner.vue";
import { isValidEmailFormat } from "../../../../libs/is-valid-email-format";

export default {
    name: "Details",
    components: {
        ImageUpload,
        ButtonComponent,
        Spinner,
    },
    mixins: [updateImage],
    emits: ["update"],
    data() {
        return {
            service: {
                name: "",
                description: "",
                img: "",
                primaryCategory: null,
                secondaryCategories: [],
                bookingPolicy: "",
                bookingEmail: "",
                bookingUrl: "",
            },
            errors: {
                name: "",
                description: "",
                img: "",
                primaryCategory: "",
                bookingPolicy: "",
                bookingEmail: "",
            },
            formSubmitted: false,
            categories: [],
        };
    },
    methods: {

        prevStep() {
            this.$router.push({ name: "Listing type" });
        },

        async setImg(img) {
            const oldImg = this.imgData.find((old) => old.imgId === this.service.img);
            const updatedImage = await this.updateImage(img, window.currentUserId, oldImg);
            const newImgData = this.imgData.filter((d) => d.imgId !== this.service.img);
            newImgData.push(updatedImage);
            this.service.img = updatedImage.imgId;
            this.syncStorageForImgData(newImgData);
            this.$storage.setStorageSync("service", this.service);
        },

        async submit() {
            let validName = false;
            let valid = false;
            try {
                if (!this.formSubmitted) {
                    this.formSubmitted = true;

                    const response = await this.checkActivityName();

                    this.formSubmitted = false;

                    if (response.data === "false" && response.errors.name) {
                        this.errors.name = response.errors.name;
                    } else {
                        validName = true;
                    }
                }
            } catch (err) {
                console.log(err);
            }

            if (validName) {
                valid = this.validate();
            } else {
                return false;
            }

            if (valid) {
                this.$storage.setStorageSync("service", this.service);
                await this.$router.push({ name: "Service ages" });
            }
        },
        checkActivityName() {
            return $.ajax({
                url: "/check-unique-activity-name-for-user",
                method: "POST",
                data: {
                    activityName: this.service.name,
                    userId: window.currentUserId,
                },
                dataType: "json",
            });
        },
        validate() {
            this.service.name === ""
                ? this.errors.name = "Service name is required"
                : this.errors.name = "";
            this.service.description === ""
                ? this.errors.description = "Service description is required"
                : this.errors.description = "";
            this.service.img === ""
                ? this.errors.img = "Image is required"
                : this.errors.img = "";
            this.service.primaryCategory === "" || this.service.primaryCategory === null
                ? this.errors.primaryCategory = "Primary category is required"
                : this.errors.primaryCategory = "";
            this.service.bookingEmail === "" || !isValidEmailFormat(this.service.bookingEmail)
                ? this.errors.bookingEmail = "Booking email is required"
                : this.errors.bookingEmail = "";

            const errors = Object.values(this.errors).filter((el) => el !== "");

            if (errors.length > 0) {
                return false;
            }
            return true;
        },
    },
    computed: {
        descriptionCharLeft() {
            return 1400 - this.service.description.length;
        },

        getActivityImg() {
            return this.imgData.find((images) => images.imgId === this.service.img)?.imgUrl || "";
        },
    },
    watch: {
        service: {
            deep: true,
            handler() {
                if (this.service.name !== "") {
                    this.errors.name = "";
                }

                if (this.service.description !== "") {
                    this.errors.description = "";
                }

                if (this.service.img !== "") {
                    this.errors.img = "";
                }

                if (this.service.primaryCategory?.category_id !== "") {
                    this.errors.primaryCategory = "";
                }

                if (this.service.bookingEmail !== "email") {
                    this.errors.bookingEmail = "";
                }

                if (this.service.primaryCategory?.category_id) {
                    const catIdx = this.service.secondaryCategories?.findIndex((el) => el.category_id === this.service.primaryCategory.category_id);

                    if (catIdx !== -1 && catIdx !== undefined) {
                        this.service.secondaryCategories.splice(catIdx, 1);
                    }
                }

                this.$emit("update", this.service);
            },
        },
    },
    mounted() {
        this.categories = window.allCategories;

        const service = this.$storage.getStorageSync("service");
        if (service) {
            this.service = service;
        }

        if (this.service.bookingEmail === "") {
            this.service.bookingEmail = window.businessEmail; // default email if not set
        }

        this.imgData = this.$storage.getStorageSync("imgData") || [];
    },
};
</script>

<template>
    <div class="details">
        <div class="mt-4 mb-5 service-steps">
            Step 1 of 5
        </div>

        <form @submit.prevent="submit">
            <div class="position-relative d-flex flex-row mt-3 header-gap">
                <h2 class="service-title">
                    Service details
                </h2>
            </div>

            <div class="d-flex flex-column">
                <label class="show">Service name<span class="required-field">*</span></label>
                <div class="info-edit-text">This will appear on your listing and in search results.</div>
                <input type="text" maxlength="45" v-model="service.name" placeholder="Enter a name"/>
                <div v-if="errors.name">
                    <div class="error-text" data-test="validation-error">{{ errors.name }}</div>
                </div>
            </div>

            <div class="mt-3 d-flex flex-column">
                <label class="show">Service description<span class="required-field">*</span></label>
                <textarea v-model="service.description" maxlength="1400" class="" placeholder="Enter a description"
                          rows="3"></textarea>
                <div class="information-text">{{ descriptionCharLeft }} characters left</div>
                <div v-if="errors.description">
                    <div class="error-text" data-test="validation-error">{{ errors.description }}</div>
                </div>
            </div>

            <ImageUpload
                block-title="Service picture"
                @cropped="setImg($event)"
                :main-img="getImgUrlById(service.img)"
            />
            <div v-if="errors.img">
                <div class="error-text" data-test="validation-error">{{ errors.img }}</div>
            </div>

            <div class="category-select mt-3">
                <label class="show">Main category<span class="required-field">*</span></label>
                <v-select
                    :options="categories"
                    label="name"
                    placeholder="Choose a category"
                    v-model="service.primaryCategory"
                />
            </div>
            <div v-if="errors.primaryCategory">
                <div class="error-text" data-test="validation-error">{{ errors.primaryCategory }}</div>
            </div>

            <div class="category-select mt-3">
                <label class="show">Other categories(optional)</label>
                <v-select
                    :options="categories"
                    label="name"
                    placeholder="Choose a category"
                    :multiple="true"
                    :selectable="(option) => option.name !== service.primaryCategory?.name"
                    v-model="service.secondaryCategories"
                />
            </div>

            <div class="d-flex flex-column mt-3">
                <label class="show">Booking policy</label>
                <textarea v-model="service.bookingPolicy" maxlength="300" class=""
                          placeholder="Enter your booking policy"
                          rows="3"></textarea>
            </div>

            <h5 class="font-weight-bolder your-details-title">Your details</h5>

            <div class="d-flex flex-column">
                <label class="show">Service email<span class="required-field">*</span></label>
                <div class="info-edit-text">Enquiries will be sent here.</div>
                <input v-model="service.bookingEmail" type="email"
                       placeholder="Enter an email address" class=""/>

                <div v-if="errors.bookingEmail">
                    <div class="error-text" data-test="validation-error">{{ errors.bookingEmail }}</div>
                </div>

                <label class="show mt-3">URL</label>
                <input v-model="service.bookingUrl" type="url" placeholder="Enter a URL"/>
            </div>

            <div class="mt-5 d-flex flex-row actions-buttons">
                <button @click="prevStep" class="ml-0 px-4 py-3 d-block btn-new btn-new-outline">
                    Back
                </button>
                <ButtonComponent
                    class="ml-auto"
                    button-type="submit"
                    type="primary"
                >
                    Next
                    <Spinner
                        v-if="formSubmitted"
                        color="white"
                    />
                </ButtonComponent>
            </div>
        </form>
    </div>
</template>

<style scoped>
.details {
    width: 469px;
    margin: 10px auto;
}

.service-title {
    font-weight: 600;
}

.header-gap {
    margin-bottom: 2.0rem;
}

.info-edit-text {
    font-style: italic;
    font-weight: normal;
    font-size: 0.875rem;
    line-height: 1.3125rem;
    color: #636066;
}

.service-type-title {
    margin-top: 30px;
    margin-bottom: 0px;
    font-style: normal;
    font-weight: 400;
    font-size: 1rem;
    line-height: 1.5rem;
    color: #636066;
}

.your-details-title {
    margin: 26px 0px;
    font-style: normal;
    font-weight: 600;
    font-size: 1.125rem;
    line-height: 1.6875rem;
    color: #272529;
}

.error-text {
    margin-top: 0.5rem;
}

</style>
